import { useCallback, useMemo } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../lib/authConfig.js';
import { acquireToken } from '../lib/tokenHelper.js';
import type {
  HoneypotDayRow,
  MetricsRow,
  RecentRewriteRow,
  RecentSendRow,
  RewriteFailureRow,
} from './types.js';

export class ApiError extends Error {
  public readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Fetch wrapper: attaches `Authorization: Bearer <MSAL access token>`. */
async function authedFetch(
  instance: IPublicClientApplication,
  account: AccountInfo | null,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  if (!account) throw new ApiError(401, 'Not signed in');
  const { accessToken } = await acquireToken(instance, { ...loginRequest, account });
  const headers = new Headers(init.headers ?? undefined);
  headers.set('accept', 'application/json');
  headers.set('authorization', `Bearer ${accessToken}`);
  return fetch(`/api${path}`, { ...init, headers });
}

export interface Api {
  metrics: () => Promise<MetricsRow>;
  recentRewrites: () => Promise<RecentRewriteRow[]>;
  rewriteFailures: () => Promise<RewriteFailureRow[]>;
  recentSends: () => Promise<RecentSendRow[]>;
  honeypotSeries: (days?: number) => Promise<HoneypotDayRow[]>;
  rewriteNow: (templateId: number) => Promise<{ ok: boolean }>;
}

/**
 * React hook returning an Api bound to the current MSAL account. The hook is
 * stable per (instance, account) pair so callers can include `api` in their
 * useEffect dependency arrays without loops.
 */
export function useApi(): Api {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);

  const get = useCallback(
    async <T,>(path: string): Promise<T> => {
      const res = await authedFetch(instance, account, path);
      if (!res.ok) throw new ApiError(res.status, `GET ${path} -> ${res.status}`);
      return (await res.json()) as T;
    },
    [instance, account],
  );

  const post = useCallback(
    async <T,>(path: string): Promise<T> => {
      const res = await authedFetch(instance, account, path, { method: 'POST' });
      if (!res.ok) throw new ApiError(res.status, `POST ${path} -> ${res.status}`);
      return (await res.json()) as T;
    },
    [instance, account],
  );

  return useMemo<Api>(
    () => ({
      metrics: () => get<MetricsRow>('/metrics'),
      recentRewrites: () => get<RecentRewriteRow[]>('/recent-rewrites'),
      rewriteFailures: () => get<RewriteFailureRow[]>('/rewrite-failures'),
      recentSends: () => get<RecentSendRow[]>('/recent-sends'),
      honeypotSeries: (days = 14) =>
        get<HoneypotDayRow[]>(`/honeypot-series?days=${days}`),
      rewriteNow: (templateId) =>
        post<{ ok: boolean }>(`/rewrite-now/${templateId}`),
    }),
    [get, post],
  );
}
